import React, { useState, useEffect, useContext } from 'react';
import {
  HeaderSearchListsSection,
  TabContainer,
  Tab,
  SectionHeader,
  SectionTitle,
  ViewAllLink,
  LoadMoreButton,
} from './styles';
import HeaderSearchItem from './HeaderSearchItem';
import { useExchangeStore } from '../../../../../hooks/trade/exchangeStore';
import { useCopyPortfolioStore } from '@/app/hooks/copyTrading/copyPortfolioStore';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';

const HeaderSearchLists = ({ search }) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const { EPs } = useExchangeStore();
  const { portfolios, fetchPortfolios, isLoading } = useCopyPortfolioStore();
  const [activeTab, setActiveTab] = useState('All');

  const tabs = ['All', 'Derivatives', 'Spot'];

  // Copy Trading 포트폴리오 로드
  useEffect(() => {
    if (search && (!portfolios || portfolios.length === 0)) {
      fetchPortfolios({
        sortBy: 'totalPnl',
        order: 'desc',
        status: 1,
        page: 1,
        limit: 20,
      });
    }
  }, [search]);

  // Derivatives 필터링 (USDT Perpetual만)
  const derivativesData = EPs?.filter(
    (item) =>
      item?.pairs?.toLowerCase()?.includes(search?.toLowerCase())
  )?.slice(0, 5) || [];

  // Copy Trading 검색 필터링
  const copyTradingData = portfolios?.filter((portfolio) => {
    const searchLower = search?.toLowerCase() || '';
    return (
      portfolio.portfolioName?.toLowerCase().includes(searchLower) ||
      portfolio.leaderUsername?.toLowerCase().includes(searchLower)
    );
  })?.slice(0, 3) || [];

  return (
    <HeaderSearchListsSection>
      {/* Tabs */}
      <TabContainer>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            active={activeTab === tab}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </Tab>
        ))}
      </TabContainer>

      {/* Content based on active tab */}
      {activeTab === 'All' && (
        <>
          {/* Derivatives Section */}
          {derivativesData.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <SectionHeader>
                <SectionTitle>Derivatives</SectionTitle>
              </SectionHeader>
              <div>
                {derivativesData.map((item, index) => (
                  <HeaderSearchItem key={item.id} item={item} index={index} />
                ))}
              </div>
            </div>
          ) : (
            search && (
              <div style={{ marginTop: 16 }}>
                <SectionHeader>
                  <SectionTitle>Derivatives</SectionTitle>
                </SectionHeader>
                <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, opacity: 0.5 }}>
                  {t('noSearchResults')}
                </div>
              </div>
            )
          )}

          {/* Copy Trading Section */}
          {copyTradingData.length > 0 ? (
            <div style={{ marginTop: 24 }}>
              <SectionHeader>
                <SectionTitle>Copy Trading</SectionTitle>
                <ViewAllLink onClick={() => window.location.href = '/copy-trade'}>
                  View All →
                </ViewAllLink>
              </SectionHeader>
              <div>
                {copyTradingData.map((portfolio) => (
                  <div
                    key={portfolio.id}
                    style={{
                      padding: '12px',
                      borderRadius: 8,
                      background: 'rgba(255, 255, 255, 0.03)',
                      cursor: 'pointer',
                      marginBottom: 8,
                    }}
                    onClick={() => {
                      window.location.href = '/copy-trade';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: portfolio.metadata?.imageUrl
                            ? `url(${portfolio.metadata.imageUrl}) center/cover`
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                          fontWeight: 700,
                          color: '#fff',
                        }}
                      >
                        {!portfolio.metadata?.imageUrl && (portfolio.portfolioName?.[0] || 'T')}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: theme.COLORS.textw }}>
                            {portfolio.portfolioName || 'Unknown'}
                          </span>
                          {portfolio.leaderUsername && (
                            <span style={{ fontSize: 12, opacity: 0.6, color: theme.COLORS.textw }}>
                              {portfolio.leaderUsername}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 16,
                            marginTop: 8,
                            fontSize: 11,
                            opacity: 0.7,
                            color: theme.COLORS.textw,
                          }}
                        >
                          <div>
                            <div>Followers</div>
                            <div style={{ fontWeight: 600, marginTop: 2 }}>
                              {portfolio.currentFollowers || 0}/{portfolio.maxFollowers || 0}
                            </div>
                          </div>
                          <div>
                            <div>Total PnL</div>
                            <div
                              style={{
                                fontWeight: 600,
                                marginTop: 2,
                                color:
                                  parseFloat(portfolio.totalPnl?.toString() || '0') > 0
                                    ? '#26a69a'
                                    : parseFloat(portfolio.totalPnl?.toString() || '0') < 0
                                    ? '#f44336'
                                    : '#999',
                              }}
                            >
                              {parseFloat(portfolio.totalPnl?.toString() || '0') > 0 ? '+' : ''}
                              {parseFloat(portfolio.totalPnl?.toString() || '0').toFixed(2)} USDT
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            search && (
              <div style={{ marginTop: 24 }}>
                <SectionHeader>
                  <SectionTitle>Copy Trading</SectionTitle>
                </SectionHeader>
                <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, opacity: 0.5 }}>
                  {t('noSearchResults')}
                </div>
              </div>
            )
          )}
        </>
      )}

      {activeTab === 'Derivatives' && (
        <>
          {derivativesData.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              {derivativesData.map((item, index) => (
                <HeaderSearchItem key={item.id} item={item} index={index} />
              ))}
            </div>
          ) : (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, opacity: 0.5 }}>
              {t('noSearchResults')}
            </div>
          )}
        </>
      )}

      {activeTab === 'Spot' && (
        <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, opacity: 0.5 }}>
          {t('noSearchResults')}
        </div>
      )}
    </HeaderSearchListsSection>
  );
};

export default HeaderSearchLists;
